import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
} from '@mui/material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { LoadingOverlay } from '../../../abelcommon/components/LoadingOverlay';
import { MiningAccount, loadBalancerAPI } from '../../../api/load-balancer';
import { useDebounce } from '@uidotdev/usehooks';
import { useTranslation } from 'react-i18next';

const isShortWallet = (wallet: String) => {
  return wallet.startsWith('abe');
};
export const AddWalletAddressModal = ({
  miningAccount,
  onSuccess,
  onError,
  onClose,
}: {
  miningAccount: MiningAccount;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
  onClose: () => void;
}) => {
  const { mutate: mutateAddWalletAddress, isLoading: isAddWalletAddressLoading } =
    loadBalancerAPI.hookWrapperMutation.useAddWalletAddress();

  const { t } = useTranslation();

  const validationSchema = yup.object({
    walletAddress: yup.string().trim().required(t('addWalletAddressModal.pleaseEnterWalletAddress')),
  });

  const formik = useFormik({
    initialValues: {
      walletAddress: '',
      outAddress: '',
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      if (isApiLoading || Object.keys(formik.errors).length > 0) return;
      let wallet: string;
      if (isShortWallet(values.walletAddress.trim())) {
        wallet = values.walletAddress.trim();
      } else {
        wallet = values.outAddress.trim();
      }
      mutateAddWalletAddress(
        { id: miningAccount.id, walletAddress: wallet },
        {
          onSuccess: () => {
            onSuccess(t('addWalletAddressModal.addWalletAddressSuccessfully'));
            onClose();
          },
          onError: (error) => {
            onError(error.message);
          },
        }
      );
    },
  });

  const debouncedWalletAddress = useDebounce(formik.values.walletAddress, 1000);

  const { isLoading: isOutWalletAddressLoading } = loadBalancerAPI.hookWrapperQuery.useGetWalletAddress(
    debouncedWalletAddress,
    !debouncedWalletAddress,
    miningAccount.id,
    {
      onSuccess: (outAddress) => {
        formik.setFieldValue('outAddress', outAddress);
      },
      onError: (error) => {
        formik.setFieldValue('outAddress', '', false);
        formik.setFieldTouched('outAddress', true, false);
        formik.setFieldError('outAddress', t('addWalletAddressModal.incorrectAddressFormat'));
        console.error(error);
        onError(t('addWalletAddressModal.incorrectAddressFormat'));
      },
    }
  );

  const isApiLoading = isAddWalletAddressLoading || isOutWalletAddressLoading;

  return (
    <Dialog
      open
      onClose={onClose}
      PaperProps={{
        component: 'form',
        onSubmit: formik.handleSubmit,
        style: {
          maxWidth: '500px',
          width: '100%',
        },
      }}
    >
      <DialogTitle>{t('addWalletAddressModal.addWalletAddress')}</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers sx={{ py: 4 }}>
        <TextField
          fullWidth
          id="wallet-address"
          name="walletAddress"
          label={
            formik.values.outAddress
              ? isShortWallet(formik.values.outAddress)
                ? t('addWalletAddressModal.longAddress')
                : t('addWalletAddressModal.shortAddress')
              : t('addWalletAddressModal.copyPasteWalletAddressHere')
          }
          value={formik.values.walletAddress}
          onChange={formik.handleChange}
          error={formik.touched.walletAddress && Boolean(formik.errors.walletAddress)}
          helperText={formik.touched.walletAddress && formik.errors.walletAddress}
          size="small"
          sx={{ mb: 2 }}
        />
        <Box sx={{ position: 'relative' }}>
          <TextField
            fullWidth
            id="outAddress"
            name="outAddress"
            label={
              formik.values.outAddress
                ? isShortWallet(formik.values.outAddress)
                  ? t('addWalletAddressModal.shortAddress')
                  : t('addWalletAddressModal.longAddress')
                : t('addWalletAddressModal.addressConversion')
            }
            value={formik.values.outAddress}
            onChange={formik.handleChange}
            error={formik.touched.outAddress && Boolean(formik.errors.outAddress)}
            helperText={formik.touched.outAddress && formik.errors.outAddress}
            size="small"
            disabled
          />
          {isOutWalletAddressLoading && (
            <Box
              sx={{
                position: 'absolute',
                right: '8px',
                top: '50%',
                transform: 'translateY(-50%)',
                width: 20,
                height: 20,
              }}
            >
              <CircularProgress size={20} />
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="info" size="small">
          {t('addWalletAddressModal.cancel')}
        </Button>
        <Button
          type="submit"
          disabled={isApiLoading || Object.keys(formik.errors).length > 0}
          variant="contained"
          size="small"
        >
          {t('addWalletAddressModal.add')}
        </Button>
      </DialogActions>
      {isAddWalletAddressLoading && <LoadingOverlay />}
    </Dialog>
  );
};
